import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import {
  getBillingProfile, updateBillingProfile,
  type BillingIdentity, type SupplierBillingProfile,
} from '@/lib/api';

const field = 'w-full rounded-lg border border-line-strong px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

const EMPTY: BillingIdentity = {
  legalName: null, taxId: null, registrationNumber: null, address: null,
  city: null, countySubdivision: null, iban: null, email: null, isVatRegistered: false,
  buyerReference: null,
};

/**
 * The supplier's company details — who easyDent invoices for advertising.
 *
 * Required before a top-up rather than after it. This is a pull flow: nothing has been collected
 * yet, so asking first costs a form, whereas taking the money first would leave an invoice we owe
 * and cannot produce.
 */
const CompanyDetails: React.FC<{ onComplete?: () => void }> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<SupplierBillingProfile | null>(null);
  const [form, setForm] = useState<BillingIdentity>(EMPTY);
  const [country, setCountry] = useState('RO');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getBillingProfile()
      .then((p) => {
        setProfile(p);
        setCountry(p.countryCode);
        setForm({
          legalName: p.legalName, taxId: p.taxId, registrationNumber: p.registrationNumber,
          address: p.address, city: p.city, countySubdivision: p.countySubdivision,
          iban: p.iban, email: p.email, isVatRegistered: p.isVatRegistered,
          buyerReference: p.buyerReference,
        });
      })
      .catch(() => setError(t('company.loadError', 'Could not load your company details.')));
  }, []);

  useEffect(load, [load]);

  const set = (key: keyof BillingIdentity) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : (e.target.value || null);
    setForm((f) => ({ ...f, [key]: v }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await updateBillingProfile(form, country);
      setSaved(true);
      const fresh = await getBillingProfile();
      setProfile(fresh);
      if (fresh.isComplete) onComplete?.();
    } catch (e: any) {
      setError(e?.response?.data?.title ?? e?.response?.data?.detail ?? t('company.saveError', 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <Card><Spinner /></Card>;

  const text = (key: keyof BillingIdentity, label: string, placeholder = '') => (
    <label className="flex flex-col text-xs text-muted-strong">
      {label}
      <input
        className={field} placeholder={placeholder}
        value={(form[key] as string | null) ?? ''} onChange={set(key)}
      />
    </label>
  );

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-body">{t('company.title', "Company details")}</h2>
      <p className="mb-3 text-xs text-muted">
        Who easyDent invoices for your advertising. Needed before you can add funds.
      </p>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}
      {saved && <Alert variant="success" className="mb-3">{t('common.saved', "Saved.")}</Alert>}

      {!profile.isComplete && (
        <Alert variant="warning" className="mb-3">
          Still needed before you can top up: {profile.missingFields.join(', ')}.
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {text('legalName', 'Registered company name')}
        {text('taxId', 'Tax id (CUI / VAT number)')}
        {text('registrationNumber', 'Trade register number')}
        {text('address', 'Registered address')}
        {text('city', 'City')}
        {text('countySubdivision', 'County (ISO 3166-2)', 'RO-CJ')}
        <label className="flex flex-col text-xs text-muted-strong">
          Country
          <input
            className={field} value={country} maxLength={2}
            onChange={(e) => { setCountry(e.target.value.toUpperCase()); setSaved(false); }}
          />
        </label>
        {text('iban', 'IBAN')}
        {text('email', 'Invoice email')}
        {text('buyerReference', 'Your purchase-order reference')}
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-body-soft">
        <input type="checkbox" checked={form.isVatRegistered} onChange={set('isVatRegistered')} />
        Registered for VAT
      </label>

      <div className="mt-3">
        <Button size="sm" loading={saving} onClick={save}>{t('common.save', "Save")}</Button>
      </div>
    </Card>
  );
};

export default CompanyDetails;
